import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CreditCard, TrendingUp, Users, XCircle } from "lucide-react";
import { KpiCard } from "@/components/common";
import { api, inr, apiError } from "@/lib/api";

export default function AdminSubscriptions() {
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState([]);

  const load = () => {
    api.get("/admin/subscriptions").then((r) => setData(r.data)).catch(() => {});
    api.get("/admin/sub-plans").then((r) => setPlans(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const savePlan = async (p, field, value) => {
    try { await api.put(`/admin/sub-plans/${p.id}`, { [field]: value }); toast.success("Plan updated"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const subAction = async (s, action) => {
    if (action === "cancel" && !window.confirm("Cancel this subscription?")) return;
    try { await api.post(`/admin/subscriptions/${s.id}/action`, { action }); toast.success(`Subscription ${action}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!data) return <div className="p-8 text-center text-ink-600">Loading…</div>;
  const k = data.kpis;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard icon={TrendingUp} label="Active" value={k.active} tone="veg" />
        <KpiCard icon={Users} label="On Trial" value={k.trial} tone="gold" />
        <KpiCard icon={CreditCard} label="MRR" value={inr(k.mrr)} tone="coral" />
        <KpiCard icon={XCircle} label="Cancelled" value={k.cancelled} tone="ink" />
      </div>

      <div className="bg-white rounded-2xl border border-border p-4">
        <h3 className="font-bold text-ink mb-3">Subscription Plans</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead><tr className="text-left text-xs text-ink-600 uppercase border-b border-border">{["Plan", "Monthly", "Yearly", "Trial (days)", "Active"].map((c) => <th key={c} className="py-2 px-2">{c}</th>)}</tr></thead>
            <tbody>
              {plans.map((p) => (
                <tr key={p.id} className="border-b border-border last:border-0" data-testid={`admin-plan-${p.key}`}>
                  <td className="py-2 px-2 font-semibold">{p.name}</td>
                  <td className="px-2"><input type="number" defaultValue={p.price_monthly} onBlur={(e) => Number(e.target.value) !== p.price_monthly && savePlan(p, "price_monthly", Number(e.target.value))} className="w-20 bg-surface rounded px-2 py-1 outline-none" data-testid={`plan-monthly-${p.key}`} /></td>
                  <td className="px-2"><input type="number" defaultValue={p.price_yearly} onBlur={(e) => Number(e.target.value) !== p.price_yearly && savePlan(p, "price_yearly", Number(e.target.value))} className="w-24 bg-surface rounded px-2 py-1 outline-none" /></td>
                  <td className="px-2"><input type="number" defaultValue={p.trial_days} onBlur={(e) => Number(e.target.value) !== p.trial_days && savePlan(p, "trial_days", Number(e.target.value))} className="w-16 bg-surface rounded px-2 py-1 outline-none" /></td>
                  <td className="px-2"><button onClick={() => savePlan(p, "active", !p.active)} className={`text-xs font-bold px-2 py-1 rounded ${p.active ? "bg-emerald-50 text-veg" : "bg-red-50 text-bad"}`} data-testid={`plan-active-${p.key}`}>{p.active ? "Active" : "Disabled"}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-border p-4">
        <h3 className="font-bold text-ink mb-3">Restaurant Subscriptions ({data.subscriptions.length})</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead><tr className="text-left text-xs text-ink-600 uppercase border-b border-border">{["Restaurant", "Plan", "Cycle", "Status", "Renews", "Actions"].map((c) => <th key={c} className="py-2 px-2">{c}</th>)}</tr></thead>
            <tbody>
              {data.subscriptions.map((s) => (
                <tr key={s.id} className="border-b border-border last:border-0" data-testid={`admin-sub-${s.id}`}>
                  <td className="py-2 px-2 font-semibold">{s.restaurant_name}</td>
                  <td className="px-2">{s.plan_name}</td>
                  <td className="px-2 capitalize">{s.cycle}</td>
                  <td className="px-2"><span className={`text-xs font-bold px-2 py-0.5 rounded-full ${s.status === "active" ? "bg-emerald-50 text-veg" : s.status === "trial" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{s.status}</span></td>
                  <td className="px-2">{s.renewal_date?.slice(0, 10)}</td>
                  <td className="px-2 flex gap-1.5 py-2">
                    {s.status !== "active" && <button onClick={() => subAction(s, "reactivate")} className="text-xs font-bold text-veg" data-testid={`sub-reactivate-${s.id}`}>Reactivate</button>}
                    {s.status === "active" && <button onClick={() => subAction(s, "suspend")} className="text-xs font-bold text-amber-700">Suspend</button>}
                    <button onClick={() => subAction(s, "extend")} className="text-xs font-bold text-ink-600">Extend</button>
                    <button onClick={() => subAction(s, "cancel")} className="text-xs font-bold text-bad">Cancel</button>
                  </td>
                </tr>
              ))}
              {data.subscriptions.length === 0 && <tr><td colSpan={6} className="text-center text-ink-600 py-6">No subscriptions yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
